import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Trash2, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { currentUnitPrice } from '../utils/pricing';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Cart = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { cart, removeFromCart, fetchCart } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return; // Wait for auth to load
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadCartProducts();
  }, [authLoading, isAuthenticated, cart]); // eslint-disable-line react-hooks/exhaustive-deps -- loadCartProducts/navigate são estáveis

  const loadCartProducts = async () => {
    try {
      setLoading(true);
      if (!cart.items || cart.items.length === 0) {
        setProducts([]);
        setLoading(false);
        return;
      }

      // Batch fetch all products in a single request
      const productIds = cart.items.map((item) => item.product_id);
      const response = await axios.post(`${API_URL}/products/batch`, productIds);
      
      const productsData = response.data.map((product) => {
        const cartItem = cart.items.find((item) => item.product_id === product.id);
        return {
          ...product,
          quantity: cartItem.quantity,
          // Use custom price if available (for customized products)
          displayPrice: currentUnitPrice(product, cartItem),
          customConfig: cartItem.custom_config || null,
        };
      });
      
      setProducts(productsData);
    } catch (error) {
      console.error('Failed to load cart products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (productId) => {
    try {
      await removeFromCart(productId);
      toast.success('Produto removido do carrinho');
    } catch (error) {
      toast.error('Erro ao remover produto');
    }
  };

  const total = products.reduce((sum, product) => sum + product.displayPrice * product.quantity, 0);

  if (authLoading || loading) {
    return (
      <div data-testid="loading-state" className="py-20 text-center">
        <p className="text-muted-foreground">Carregando carrinho...</p>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div data-testid="empty-cart" className="py-20 md:py-32 text-center">
        <div className="px-6 md:px-12 lg:px-24">
          <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight mb-4">
            Seu carrinho está vazio
          </h1>
          <p className="text-base font-light text-muted-foreground mb-12">
            Explore nosso catálogo e adicione móveis incríveis
          </p>
          <Link
            to="/catalogo"
            data-testid="continue-shopping"
            className="inline-flex items-center gap-3 bg-primary text-white px-8 py-4 uppercase tracking-widest text-xs hover:bg-black transition-all duration-500"
          >
            Ir para o Catálogo
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="py-20 md:py-32">
      <div className="px-6 md:px-12 lg:px-24">
        <h1 data-testid="cart-title" className="font-heading text-4xl md:text-5xl font-light tracking-tight mb-16 text-center">
          Seu Carrinho
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-8">
            {products.map((product) => (
              <div
                key={product.id}
                data-testid={`cart-item-${product.id}`}
                className="flex gap-6 pb-8 border-b border-border"
              >
                <div className="w-32 h-40 flex-shrink-0 overflow-hidden">
                  <img
                    src={product.images[0] || '/placeholder.svg'}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="flex-1">
                  <Link
                    to={`/produto/${product.id}`}
                    className="font-body text-lg font-normal hover:text-primary/70 transition-colors duration-300"
                  >
                    {product.name}
                  </Link>
                  <p className="text-sm text-muted-foreground mt-1">
                    {product.category} • {product.type}
                  </p>
                  {/* Show customization details if available */}
                  {product.customConfig && (
                    <div className="text-xs text-muted-foreground mt-2 space-y-1">
                      {product.customConfig.fabric && (
                        <p>Tecido: {product.customConfig.fabric.name}</p>
                      )}
                      {product.customConfig.size && (
                        <p>Tamanho: {product.customConfig.size.name}</p>
                      )}
                      {product.customConfig.material && (
                        <p>Material: {product.customConfig.material.name}</p>
                      )}
                    </div>
                  )}
                  <p className="text-base font-medium mt-3">
                    R$ {product.displayPrice.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    {product.promotional_price && product.displayPrice === product.promotional_price ? (
                      <span data-testid={`cart-original-price-${product.id}`} className="text-xs text-muted-foreground ml-2 line-through">
                        R$ {(product.original_price || product.price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    ) : product.customConfig && product.displayPrice < product.price && (
                      <span className="text-xs text-muted-foreground ml-2 line-through">
                        R$ {product.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">Quantidade: {product.quantity}</p>
                </div>

                <button
                  onClick={() => handleRemove(product.id)}
                  data-testid={`remove-item-${product.id}`}
                  className="text-muted-foreground hover:text-destructive transition-colors duration-300"
                >
                  <Trash2 className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="border border-border p-8">
              <h2 className="font-accent text-xs uppercase tracking-widest mb-8">Resumo do Pedido</h2>

              <div className="space-y-4 mb-8 pb-8 border-b border-border">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span data-testid="subtotal">R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>

              <div className="flex justify-between text-lg font-medium mb-8">
                <span>Total</span>
                <span data-testid="total">R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </div>

              <Link
                to="/checkout"
                data-testid="checkout-button"
                className="w-full flex items-center justify-center gap-3 bg-primary text-white px-8 py-4 uppercase tracking-widest text-xs hover:bg-black transition-all duration-500 mb-3"
              >
                Pagar com Cartão ou Pix
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>

              <Link
                to="/contato"
                data-testid="request-quote-button"
                className="w-full flex items-center justify-center gap-3 border border-primary text-primary px-8 py-4 uppercase tracking-widest text-xs hover:bg-primary hover:text-white transition-all duration-500"
              >
                Solicitar Orçamento
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>

              <p className="text-xs text-center text-muted-foreground mt-6">
                Pagamento seguro processado pelo Mercado Pago
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;